import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'sonner';

const BASE_URL = import.meta.env.VITE_APP_API_URL;
const getCustomerToken = () => localStorage.getItem('authx_customer_token');
const getAdminToken = () => localStorage.getItem('authx_access_token');

const customerApi = axios.create({ baseURL: BASE_URL });
customerApi.interceptors.request.use(cfg => {
  const t = getCustomerToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const adminApi = axios.create({ baseURL: BASE_URL });
adminApi.interceptors.request.use(cfg => {
  const t = getAdminToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const t = (name, fn) => createAsyncThunk(name, async (arg, { rejectWithValue }) => {
  try { return await fn(arg); }
  catch (err) {
    toast.error(err.response?.data?.message || err.message);
    return rejectWithValue(err.response?.data?.message || err.message);
  }
});

// Admin
export const fetchAllDraws = t('draw/fetchAll', async () => {
  const { data } = await adminApi.get('LuckyDraw');
  return data.data;
});

export const createDraw = t('draw/create', async (dto) => {
  const { data } = await adminApi.post('LuckyDraw', dto);
  toast.success('Lucky draw created!');
  return data.data;
});

export const executeDraw = t('draw/execute', async (id) => {
  const { data } = await adminApi.post(`LuckyDraw/${id}/execute`);
  toast.success(data.message);
  return id;
});

export const deleteDraw = t('draw/delete', async (id) => {
  await adminApi.delete(`LuckyDraw/${id}`);
  toast.success('Draw deleted');
  return id;
});

// Customer / Public
export const fetchActiveDraws = t('draw/fetchActive', async ({ companyId, customerId }) => {
  const params = new URLSearchParams();
  if (customerId) params.append('customerId', customerId);
  const { data } = await customerApi.get(`LuckyDraw/active/${companyId}?${params}`);
  return data.data;
});

export const fetchDrawDetail = t('draw/fetchDetail', async ({ id, companyId, customerId }) => {
  const params = new URLSearchParams({ companyId });
  if (customerId) params.append('customerId', customerId);
  const { data } = await customerApi.get(`LuckyDraw/${id}/public?${params}`);
  return data.data;
});

const luckyDrawSlice = createSlice({
  name: 'luckyDraw',
  initialState: {
    draws: [],
    activeDraws: [],
    selectedDraw: null,
    loading: false,
    actionLoading: false,
  },
  reducers: {
    clearSelectedDraw: (s) => { s.selectedDraw = null; }
  },
  extraReducers: (b) => {
    b
      .addCase(fetchAllDraws.pending, (s) => { s.loading = true; })
      .addCase(fetchAllDraws.fulfilled, (s, a) => { s.loading = false; s.draws = a.payload || []; })
      .addCase(fetchAllDraws.rejected, (s) => { s.loading = false; })
      .addCase(createDraw.fulfilled, (s, a) => { s.draws = [a.payload, ...s.draws]; })
      .addCase(executeDraw.fulfilled, (s, a) => {
        s.draws = s.draws.map(d =>
          d.drawId === a.payload ? { ...d, status: 'completed' } : d);
      })
      .addCase(deleteDraw.fulfilled, (s, a) => {
        s.draws = s.draws.filter(d => d.drawId !== a.payload);
      })
      .addCase(fetchActiveDraws.pending, (s) => { s.loading = true; })
      .addCase(fetchActiveDraws.fulfilled, (s, a) => { s.loading = false; s.activeDraws = a.payload || []; })
      .addCase(fetchActiveDraws.rejected, (s) => { s.loading = false; })
      .addCase(fetchDrawDetail.fulfilled, (s, a) => { s.selectedDraw = a.payload; });
  }
});

export const { clearSelectedDraw } = luckyDrawSlice.actions;
export default luckyDrawSlice.reducer;